import {
  groupOptionList,
  groupSelectionRows,
  normalizeOptionSelection,
  type ExtrasLimitConfig,
  type FreeOptionConfig,
  type ModifierGroup,
  type ModifierOption,
  type Money,
  type OptionPick,
  type OptionSelection,
  type OptionSelectionRow,
} from "@workspace/model"

import {
  isExtrasGroup,
  isOptionFree,
  optionPriceDelta,
  pricingSizeGroupOf,
  canonicalSizeId,
  resolveFreeOptionAvailability,
  resolveFreeOptionDefaultSet,
  type FreeOptionLine,
} from "@/lib/free-option-preview"

/**
 * CLIENT-SIDE mirror of `priceOptionSelection` (@workspace/ordering
 * `item-options.ts`) for the item customizer's live total. The selection RULES
 * themselves are not mirrored — both sides import the one engine from
 * `@workspace/model` (`item-option-selection.ts`); only the money half and the
 * legacy item-level free-option layer are copied here, because the pricing
 * engine is never imported from a `"use client"` file (CLAUDE.md).
 *
 * NON-AUTHORITATIVE: the add-to-cart action re-prices with the real engine.
 * Any change to `item-options.ts` MUST be mirrored here (and vice versa).
 */

export interface OptionPricingConfig {
  freeOption?: FreeOptionConfig
  extrasLimit?: ExtrasLimitConfig
}

export interface PreviewRow extends OptionSelectionRow {
  /** Charged per unit — 0 when free or implied by a select-all. */
  unitPrice: Money
}

/** Mirrors `isLegacyFreeOptionGroup`. */
export function isLegacyFreeOptionGroup(
  group: ModifierGroup,
  config: OptionPricingConfig | undefined
): boolean {
  return (
    group.selection.kind === "multi" &&
    config?.freeOption != null &&
    group.freeOption == null
  )
}

/** Mirrors `extrasAllowList`. */
export function extrasAllowList(
  group: ModifierGroup,
  config: OptionPricingConfig | undefined
): Set<string> | null {
  return group.selection.kind === "multi" &&
    isExtrasGroup(group) &&
    config?.extrasLimit?.limitEnabled
    ? new Set(config.extrasLimit.allowedOptionIds ?? [])
    : null
}

/** Mirrors `legacyFreeOptionDefaultIds`. */
export function legacyFreeOptionDefaultIds(
  group: ModifierGroup,
  freeOption: FreeOptionConfig
): string[] {
  const options = groupOptionList(group)
  const defaults = resolveFreeOptionDefaultSet(
    options,
    freeOption,
    group.defaultOptionIds
  )
  return options.filter((o) => defaults.has(o.id)).map((o) => o.id)
}

/** Legacy running availability over a group's explicit picks + removed defaults. */
export function legacyFreeOptionLines(
  group: ModifierGroup,
  picks: readonly OptionPick[],
  freeOption: FreeOptionConfig
): FreeOptionLine[] {
  const explicit = picks.filter((pick) => !pick.includedInOptionId)
  const explicitIds = new Set(explicit.map((pick) => pick.optionId))
  return [
    ...legacyFreeOptionDefaultIds(group, freeOption)
      .filter((id) => !explicitIds.has(id))
      .map((id): FreeOptionLine => ({ optionId: id, negative: true })),
    ...explicit.map((pick): FreeOptionLine => ({ optionId: pick.optionId })),
  ]
}

/** Whether adding `optionId` next to a legacy-governed group would be free. */
export function legacyNextUnitFree(
  group: ModifierGroup,
  picks: readonly OptionPick[],
  freeOption: FreeOptionConfig,
  optionId: string
): boolean {
  return isOptionFree(
    resolveFreeOptionAvailability(
      freeOption,
      legacyFreeOptionLines(group, picks, freeOption)
    ),
    optionId
  )
}

function legacyGroupRows(
  group: ModifierGroup,
  picks: readonly OptionPick[],
  freeOption: FreeOptionConfig
): OptionSelectionRow[] {
  const explicit = picks.filter((pick) => !pick.includedInOptionId)
  const explicitIds = new Set(explicit.map((pick) => pick.optionId))
  const freeUnits = new Map<string, number>()
  let running: FreeOptionLine[] = legacyFreeOptionDefaultIds(group, freeOption)
    .filter((id) => !explicitIds.has(id))
    .map((id) => ({ optionId: id, negative: true }))
  for (const pick of explicit) {
    const availability = resolveFreeOptionAvailability(freeOption, running)
    if (isOptionFree(availability, pick.optionId)) {
      freeUnits.set(pick.optionId, (freeUnits.get(pick.optionId) ?? 0) + 1)
    }
    running = [...running, { optionId: pick.optionId }]
  }
  return groupSelectionRows({ ...group, freeOption: undefined }, picks).flatMap(
    (row): OptionSelectionRow[] => {
      if (row.includedInOptionId) return [row]
      const free = Math.min(freeUnits.get(row.option.id) ?? 0, row.quantity)
      return [
        ...(free > 0 ? [{ ...row, quantity: free, free: true }] : []),
        ...(row.quantity > free
          ? [{ ...row, quantity: row.quantity - free, free: false }]
          : []),
      ]
    }
  )
}

/** Mirrors `resolvePricedSelection`. */
export function resolvePreviewSelection(
  groups: readonly ModifierGroup[],
  selection: OptionSelection,
  config?: OptionPricingConfig
): OptionPick[][] {
  return normalizeOptionSelection(groups, selection).map((picks, index) => {
    const group = groups[index]
    const allowed = group ? extrasAllowList(group, config) : null
    if (!group) return []
    if (!allowed) return picks
    return picks.filter(
      (pick) =>
        allowed.has(pick.optionId) &&
        (!pick.includedInOptionId || allowed.has(pick.includedInOptionId))
    )
  })
}

/** Mirrors `selectionSizeId`. */
export function previewSizeId(
  groups: readonly ModifierGroup[],
  selection: OptionSelection
): string | undefined {
  const sizeGroup = pricingSizeGroupOf(groups)
  if (!sizeGroup) return undefined
  const ids = new Set(
    selection.flatMap((picks) =>
      picks.filter((p) => !p.includedInOptionId).map((p) => p.optionId)
    )
  )
  const selected = groupOptionList(sizeGroup).find((o) => ids.has(o.id))
  return selected ? canonicalSizeId(selected) : undefined
}

/** Mirrors `priceOptionSelection`; rows come back per group. */
export function previewOptionSelection(
  basePrice: Money,
  groups: readonly ModifierGroup[],
  selection: OptionSelection,
  config?: OptionPricingConfig
): { unitPrice: Money; rowsByGroup: PreviewRow[][]; sizeId?: string } {
  const currency = basePrice.currency
  const picksByGroup = resolvePreviewSelection(groups, selection, config)
  const sizeId = previewSizeId(groups, picksByGroup)
  let amount = basePrice.amount

  const rowsByGroup = groups.map((group, index) => {
    const picks = picksByGroup[index] ?? []
    const rows =
      config?.freeOption && isLegacyFreeOptionGroup(group, config)
        ? legacyGroupRows(group, picks, config.freeOption)
        : groupSelectionRows(group, picks)
    return rows.map((row): PreviewRow => {
      const unitPrice =
        row.free || row.includedInOptionId
          ? { amount: 0, currency }
          : optionPriceDelta(row.option, sizeId)
      amount += unitPrice.amount * row.quantity
      return { ...row, unitPrice }
    })
  })

  return {
    unitPrice: { amount, currency },
    rowsByGroup,
    ...(sizeId ? { sizeId } : {}),
  }
}

/** What one option adds to ONE unit of the line, summed over its rows. */
export function optionAmount(
  rows: readonly PreviewRow[],
  option: ModifierOption
): number {
  return rows
    .filter((row) => row.option.id === option.id && !row.includedInOptionId)
    .reduce((sum, row) => sum + row.unitPrice.amount * row.quantity, 0)
}
